import { useEffect, useMemo, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams } from 'react-router';
import isEmpty from 'lodash/isEmpty';
import { toast } from 'react-toastify';

import { Form } from '@radix-ui/react-form';
import { Button } from '@querodelivery/uni-react';
import styles from './styles.module.scss';

import yup from '@/infra/YupDecorator';
import PlaceFareCard from '@/Components/fare/place-fare-card/PlaceFareCard';
import type { IPlaceMinimalFareInfo } from '@/Interfaces/Data';
import { useGetPlaces } from '@/_features/places/api/get-places';
import { queryClient } from '@/_config/lib/react-query/query-client';
import { useGetPlaceFares } from '@/_features/places/api/get-place-fares';
import { GET_PLACE_FARE } from '@/features/fare/api/get-place-fare';
import { useSharePlaceFare } from '@/features/fare/api/post-share-place-fare';
import { Skeleton } from '@/_config/lib/react-native-skeleton';
import { TextInput } from '@/_components/text-input/text-input';
import ExportFareConfirmModal from './components/export-fare-confirm-modal/export-fare-confirm-modal';
import ExportFareModal from './components/export-fare-modal/export-fare-modal';
import { TableEmptyState } from '@/_components/table-empty-state/table-empty-state';

const PlaceFaresFormSchema = yup.object({
  name: yup.string().optional(),
});

type TypePlaceFaresForm = yup.InferType<typeof PlaceFaresFormSchema>;

function PlaceFares() {
  const navigate = useNavigate();
  const params = useParams();

  const cityId = useMemo(() => {
    if (params && params.cityId) {
      const { cityId } = params;

      return cityId;
    }

    return '';
  }, []);

  const { mutate: sharePlaceFare, isLoading: isSharingPlaceFare } =
    useSharePlaceFare();

  const {
    data: placeFares,
    isLoading: isLoadingFaresList,
    refetch,
  } = useGetPlaceFares({ cityId });

  const STALE_TIME_ONE_HOUR_FOR_PLACES = 1000 * 60 * 60 * 24;

  const { data: placesResponse } = useGetPlaces(
    {
      cityId,
      limit: 1000,
    },
    {
      enabled: !!cityId,
      staleTime: STALE_TIME_ONE_HOUR_FOR_PLACES,
    },
  );

  const [searchResults, setSearchResults] = useState(placeFares);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExportConfirmModalOpen, setIsExportConfirmModalOpen] =
    useState(false);
  const [currentExportPlaceId, setCurrentExportPlaceId] = useState('');
  const [selectedPlaceIdsToExport, setSelectedPlaceIdsToExport] = useState<
    string[]
  >([]);

  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<TypePlaceFaresForm>({
    defaultValues: { name: '' },
    resolver: yupResolver(PlaceFaresFormSchema),
  });

  useEffect(() => {
    handleGetFareByCityName(getValues());
  }, [placeFares]);

  const handleSubmitForm: SubmitHandler<TypePlaceFaresForm> = data => {
    handleGetFareByCityName(data);
  };

  function handleGetFareByCityName(data: TypePlaceFaresForm) {
    const { name } = data;

    if (!name) {
      return setSearchResults(placeFares);
    }

    const newSearchResults: IPlaceMinimalFareInfo[] | undefined =
      placeFares?.filter(placeFare => {
        const placeName = placeFare?.nomeExibicao?.toLowerCase();
        const searchName = name.toLowerCase();

        return placeName?.includes(searchName);
      });

    setSearchResults(newSearchResults);
  }

  const placeOptionsToExport = useMemo(
    () =>
      (placesResponse?.data || [])
        .filter(place => place._id !== currentExportPlaceId)
        .map(place => ({
          key: place._id,
          text: place.nomeExibicao,
          value: place._id,
        })),
    [placesResponse, currentExportPlaceId],
  );

  const selectedPlaceNamesToExport = useMemo(() => {
    const placeOptionsMap = new Map(
      placeOptionsToExport.map(option => [option.value, option.text]),
    );

    return selectedPlaceIdsToExport
      .map(placeId => placeOptionsMap.get(placeId))
      .filter(Boolean) as string[];
  }, [placeOptionsToExport, selectedPlaceIdsToExport]);

  const faresQuantity = searchResults?.length || 0;

  function handleCloseExportFlow() {
    setIsExportModalOpen(false);
    setIsExportConfirmModalOpen(false);
    setCurrentExportPlaceId('');
    setSelectedPlaceIdsToExport([]);
  }

  function handleOpenExportModal(placeId: string) {
    setCurrentExportPlaceId(placeId);
    setSelectedPlaceIdsToExport([]);
    setIsExportModalOpen(true);
  }

  function handleConfirmSelectedPlacesToExport() {
    if (!selectedPlaceIdsToExport.length) {
      return;
    }

    setIsExportModalOpen(false);
    setIsExportConfirmModalOpen(true);
  }

  function handleBackToExportSelection() {
    setIsExportConfirmModalOpen(false);
    setIsExportModalOpen(true);
  }

  function handleExportFares() {
    if (!currentExportPlaceId) {
      toast.warn('Não foi possível identificar o estabelecimento de origem.');
      return;
    }

    sharePlaceFare(
      {
        placeId: currentExportPlaceId,
        placeIds: selectedPlaceIdsToExport,
      },
      {
        onSuccess: () => {
          refetch();
          queryClient.refetchQueries([GET_PLACE_FARE]);
          handleCloseExportFlow();
        },
      },
    );
  }

  return (
    <div className="container">
      <div className={styles.placeFaresHeader}>
        <Form
          className={styles.search}
          onSubmit={handleSubmit(handleSubmitForm)}
        >
          <Controller
            name="name"
            control={control}
            render={({ field: { value, onChange } }) => (
              <TextInput
                name="name"
                label="Estabelecimento"
                placeholder="Digite o nome do estabelecimento"
                autoFocus
                value={value}
                onChange={event => onChange(event.target.value)}
                errorMessage={errors.name?.message}
              />
            )}
          />

          <Button
            type="submit"
            variant="outline"
            size="lg"
            style={{ maxWidth: '7.75rem' }}
          >
            <span className="font-medium">Buscar</span>
          </Button>
        </Form>

        <Button type="button" size="lg" onClick={() => navigate(-1)}>
          <span className="font-medium">Voltar</span>
        </Button>
      </div>

      <div>
        {isLoadingFaresList ? (
          <div className={styles.skeletonContainer}>
            <Skeleton
              height={17}
              width={100}
              borderRadius={8}
              className={styles.totalCount}
            />
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} height={91} borderRadius={20} />
            ))}
          </div>
        ) : (
          <>
            <p className="text-right mgn-bottom-sm">{faresQuantity} tarifas</p>

            <div className={styles.faresList}>
              {!searchResults || isEmpty(searchResults) ? (
                <TableEmptyState
                  title="Nenhuma tarifa encontrada"
                  description="Não encontramos tarifas de parceiros para esse município."
                />
              ) : (
                searchResults?.map(fare => {
                  if (!fare.placeFare) {
                    return null;
                  }

                  return (
                    <PlaceFareCard
                      key={fare._id}
                      fare={fare}
                      updateFares={refetch}
                      onExportFare={handleOpenExportModal}
                    />
                  );
                })
              )}
            </div>
          </>
        )}
      </div>

      <ExportFareModal
        open={isExportModalOpen}
        onClose={handleCloseExportFlow}
        options={placeOptionsToExport}
        selectedPlaceIds={selectedPlaceIdsToExport}
        onSelectionChange={(value: string | string[]) => {
          setSelectedPlaceIdsToExport(Array.isArray(value) ? value : []);
        }}
        onConfirm={handleConfirmSelectedPlacesToExport}
      />

      <ExportFareConfirmModal
        open={isExportConfirmModalOpen}
        onClose={handleCloseExportFlow}
        onCancel={handleBackToExportSelection}
        onConfirm={handleExportFares}
        selectedPlaceNames={selectedPlaceNamesToExport}
        loading={isSharingPlaceFare}
      />
    </div>
  );
}

export default PlaceFares;
